/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import { useEvent } from '@react-aria/utils';
import * as React from 'react';
import { useObjectRef } from 'react-aria';
import {
    CheckboxContext,
    type ContextValue,
    HeadingContext,
    SeparatorContext,
    useContextProps,
} from 'react-aria-components';

import { useEventCallback, useLiveRef } from '@gitbook/util-react-hooks';
import { assert, assertNever } from '@gitbook/util-ts';

import { OverlayTriggerContext, useOverlayClose } from '../Overlay';
import { findClosestPopoverElement } from '../Popover';
import { getSpineElementRect } from '../util';
import { AutoComplete, AutoCompleteStateContext } from './AutoComplete';
import { useFilterMenuNodes } from './MenuFilter';
import { type MenuItemAPI, MenuItemContext, MenuItemTitlePrefix } from './MenuItem';
import { MenuPlaceholder } from './MenuPlaceholder';
import { HiddenMenuSearch } from './MenuSearch';
import {
    MENU_MAX_HEIGHT,
    MENU_MAX_WIDTH,
    MENU_MIN_WIDTH,
    MENU_VIRTUAL_PADDING_END,
    MENU_VIRTUAL_PADDING_START,
} from './MenuSizing';
import { type MenuState, MenuStateContext } from './MenuState';
import { MenuVirtualRenderer, type MenuVirtualRendererRef } from './MenuVirtualRenderer';
import { SubMenuContext, type SubMenuRef } from './SubMenu';
import { type AnyMenuNode, estimateMenuNodeSize, getMenuTreeFromChildren } from './tree';

type MenuProps = {
    /**
     * Children that defines the content of the menu.
     * A menu accepts strict children, they are not rendered but the tree is traversed.
     */
    children: React.ReactNode;

    /**
     * Freeze the children once opened.
     * Useful when a list is muted via `onAction` and you
     * don’t want changes to be reflected while the list remains open.
     */
    frozen?: boolean;

    /**
     * Id of the menu.
     */
    id?: string | undefined;

    /**
     * Placeholder displayed when there is no items displayed.
     * @default "No items"
     */
    emptyPlaceholder?: string | undefined;

    /**
     * Placeholder displayed when there is no results from a search.
     * @default "No matching items"
     */
    noResultsPlaceholder?: string | undefined;

    /**
     * How the items of this menu display their `checked` state: a trailing check glyph (`icon`,
     * the default), or a persistent row highlight with a bold title (`highlight`).
     * @default "icon"
     */
    checkedIndicator?: 'icon' | 'highlight';
};

export const MenuContext = React.createContext<ContextValue<Partial<MenuProps>, HTMLDivElement>>(
    {}
);

export function Menu({ ref, ...props }: MenuProps & { ref?: React.ForwardedRef<HTMLDivElement> }) {
    const autoComplete = React.useContext(AutoCompleteStateContext);
    // A menu's checkboxes must not inherit a surrounding table's selection slot.
    const menu = (
        <CheckboxContext.Provider value={null}>
            <InternalMenu ref={ref} {...props} />
        </CheckboxContext.Provider>
    );
    if (autoComplete) {
        return menu;
    }
    return (
        <AutoComplete>
            <HiddenMenuSearch />
            {menu}
        </AutoComplete>
    );
}

function getScrollHintContentMaxHeight(items: AnyMenuNode[], desiredContentHeight: number): number {
    let offset = MENU_VIRTUAL_PADDING_START;
    let contentMaxHeight = desiredContentHeight;

    for (const item of items) {
        const { height } = estimateMenuNodeSize(item);
        const clipPoint = offset + height / 2;
        if (clipPoint <= desiredContentHeight) {
            contentMaxHeight = clipPoint;
        }
        offset += height;
        if (offset + MENU_VIRTUAL_PADDING_END > desiredContentHeight) {
            return contentMaxHeight;
        }
    }

    return desiredContentHeight;
}

const InternalMenu = function InternalMenu({
    ref,
    ...props
}: MenuProps & { ref?: React.ForwardedRef<HTMLDivElement> }) {
    [props, ref] = useContextProps(props, ref, MenuContext);
    const {
        children: baseChildren,
        id: idProp,
        frozen,
        emptyPlaceholder = 'No items',
        noResultsPlaceholder = 'No matching items',
        checkedIndicator,
    } = props;
    const [initialChildren] = React.useState(baseChildren);
    const children = frozen ? initialChildren : baseChildren;
    const trigger = React.useContext(OverlayTriggerContext);
    assert(trigger);
    const defaultId = React.useId();
    const id = idProp ?? defaultId;
    const menuRef = useObjectRef(ref);
    const parentState = React.useContext(MenuStateContext);
    const [localFocusedId, localSetFocusedId] = React.useState<string | null>(id);
    const [minWidth, setMinWidth] = React.useState(0);
    const updateMinWidth = useEventCallback((width: number) => {
        const nextWidth = Math.min(Math.max(width, MENU_MIN_WIDTH), MENU_MAX_WIDTH);
        setMinWidth((x) => (x < nextWidth ? nextWidth : x));
    });
    const [isSubmenuOpen, setSubmenuOpen] = React.useState(false);
    const autoComplete = React.useContext(AutoCompleteStateContext);
    const autoCompleteInputRef =
        autoComplete?.externalInputRef ?? autoComplete?.internalSearchInputRef;
    // Set the min-width to the search input size if
    // the input it outside the menu popover.
    React.useLayoutEffect(() => {
        const searchInputElement = autoCompleteInputRef?.current;
        const menuElement = menuRef.current;
        if (!menuElement || !searchInputElement) {
            return;
        }

        // Only size based on input.
        if (searchInputElement.tagName !== 'INPUT') {
            return;
        }

        const popoverElement = findClosestPopoverElement(menuElement);
        if (popoverElement?.contains(searchInputElement)) {
            return;
        }

        const { width } = getSpineElementRect(searchInputElement);
        updateMinWidth(width);
    }, [updateMinWidth, autoCompleteInputRef, menuRef]);
    const allNodes = React.useMemo(() => getMenuTreeFromChildren(children, id), [children, id]);
    const filterValue = autoComplete?.filterValue ?? null;
    const items = useFilterMenuNodes(allNodes, filterValue);
    const getCachedScrollHintContentMaxHeight = React.useMemo(() => {
        const cache = new Map<number, number>();
        return (desiredContentHeight: number) => {
            const cachedHeight = cache.get(desiredContentHeight);
            if (cachedHeight !== undefined) {
                return cachedHeight;
            }

            const contentMaxHeight = getScrollHintContentMaxHeight(items, desiredContentHeight);
            cache.set(desiredContentHeight, contentMaxHeight);
            return contentMaxHeight;
        };
    }, [items]);
    const focusableItems = React.useMemo(
        () => items.filter((item) => item.type === 'item' && !item.disabled),
        [items]
    );
    const isEmpty = allNodes.length === 0;
    const hasNoMatches = filterValue && items.length === 0;
    const isReady = Boolean(minWidth > 0 || isEmpty || hasNoMatches);

    const defaultFocusedItemId = focusableItems[0]?.id ?? null;
    const [focusedItemId, setFocusedItemId] = React.useState<string | null>(defaultFocusedItemId);
    const itemAPIRef = React.useRef<MenuItemAPI>(null);
    React.useEffect(() => {
        setFocusedItemId(defaultFocusedItemId);
    }, [defaultFocusedItemId, filterValue]);

    const state = React.useMemo<MenuState>(() => {
        const setFocusedId = parentState?.setFocusedId ?? localSetFocusedId;
        const focus: MenuState['focus'] = (options) => {
            setFocusedId(id);
            clearTimeout(openSubmenuTimerRef.current);
            if (options?.closeSubmenu) {
                setSubmenuOpen(false);
            }
        };
        if (parentState) {
            return {
                ...parentState,
                focus,
            };
        }
        return { id, focusedId: localFocusedId, setFocusedId, focus };
    }, [parentState, localFocusedId, id]);

    const isParentFocused = state.focusedId === parentState?.id;
    const isFocused = state.focusedId === id;
    const anchorElementRef = autoCompleteInputRef ?? menuRef;
    React.useEffect(() => {
        if (isParentFocused) {
            setSubmenuOpen(false);
        }
    }, [isParentFocused]);
    React.useEffect(() => {
        if (isFocused && isReady) {
            if (autoComplete?.internalSearchInputRef?.current) {
                autoComplete.internalSearchInputRef.current.focus();
            } else if (anchorElementRef.current === menuRef.current) {
                menuRef.current?.focus();
            } else if (
                anchorElementRef.current instanceof HTMLInputElement ||
                anchorElementRef.current instanceof HTMLTextAreaElement
            ) {
                anchorElementRef.current?.focus();
            }
        }
    }, [isFocused, isReady, menuRef, anchorElementRef, autoComplete?.internalSearchInputRef]);
    const refs = useLiveRef({ isFocused });
    const submenuRef = React.useRef<SubMenuRef>(null);

    const rendererRef = React.useRef<MenuVirtualRendererRef>(null);
    const moveToItemId = React.useCallback(
        (id: string) => {
            setFocusedItemId(id);
            // Safari re-snaps to a stale row whenever the virtualized list re-renders, including
            // after snapping is turned back on, so it stays off for the rest of this menu.
            menuRef.current?.setAttribute('data-keyboard-scrolled', '');
            rendererRef.current?.scrollToId(id);
        },
        [menuRef]
    );
    // Reset the scroll position to the top when the query changes so a new search starts at the
    // first result.
    React.useEffect(() => {
        rendererRef.current?.scrollToTop();
    }, [filterValue]);
    const goTo = (step: number) => {
        const index = focusableItems.findIndex((item) => item.id === focusedItemId);
        if (index === -1) {
            const firstItem = focusableItems[0];
            if (!firstItem?.id) {
                return;
            }
            moveToItemId(firstItem.id);
            return;
        }
        let nextIndex = index + step;
        if (nextIndex < 0) {
            nextIndex = focusableItems.length - 1;
        } else if (nextIndex >= focusableItems.length) {
            nextIndex = 0;
        }
        const nextItem = focusableItems[nextIndex];
        assert(nextItem?.id);
        moveToItemId(nextItem.id);
    };
    useEvent(
        anchorElementRef,
        'mouseover',
        () => {
            state.focus();
        },
        { capture: true }
    );
    useEvent(
        anchorElementRef,
        'keydown',
        (event) => {
            if (!isFocused) {
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                trigger.close();
                return;
            }

            if (event.key === 'Enter') {
                event.preventDefault();
                event.stopPropagation();
                itemAPIRef.current?.runAction();
                return;
            }

            if (
                event.code === 'Space' &&
                (!autoComplete ||
                    (!autoComplete.isTouched &&
                        !autoComplete.value &&
                        // If autocomplete is used with an external input, we never allow space.
                        !autoComplete.externalInputRef))
            ) {
                event.preventDefault();
                event.stopPropagation();
                itemAPIRef.current?.runAction();
                return;
            }

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                event.stopPropagation();
                goTo(1);
                setSubmenuOpen(false);
                clearTimeout(openSubmenuTimerRef.current);
                openSubmenuTimerRef.current = window.setTimeout(() => {
                    setSubmenuOpen(true);
                }, 300);
                return;
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault();
                event.stopPropagation();
                goTo(-1);
                setSubmenuOpen(false);
                clearTimeout(openSubmenuTimerRef.current);
                openSubmenuTimerRef.current = window.setTimeout(() => {
                    setSubmenuOpen(true);
                }, 300);
                return;
            }

            if (event.key === 'ArrowRight') {
                const focusedItem = focusableItems.find((item) => item.id === focusedItemId);
                if (!focusedItem || focusedItem.type !== 'item') {
                    return;
                }

                if (!focusedItem.children?.length) {
                    return;
                }

                if (event.target instanceof HTMLInputElement) {
                    const { selectionStart, selectionEnd, value } = event.target;
                    const atEnd = selectionStart === value.length && selectionEnd === value.length;
                    if (!atEnd) {
                        return;
                    }
                }

                event.preventDefault();
                event.stopPropagation();
                submenuRef.current?.focus();
                setSubmenuOpen(true);
                clearTimeout(openSubmenuTimerRef.current);
                return;
            }

            if (event.key === 'ArrowLeft' && parentState) {
                if (event.target instanceof HTMLInputElement) {
                    const { selectionStart, selectionEnd } = event.target;
                    const atStart = selectionStart === 0 && selectionEnd === 0;
                    if (!atStart) {
                        return;
                    }
                }

                event.preventDefault();
                event.stopPropagation();
                parentState.focus({ closeSubmenu: true });
                return;
            }
        },
        { capture: true }
    );
    const closeOverlay = useOverlayClose();
    const latestPointerMoveEventRef = React.useRef<React.PointerEvent | null>(null);
    const openSubmenuTimerRef = React.useRef(0);

    React.useLayoutEffect(() => {
        const menuElement = menuRef.current;
        const popoverElement = menuElement ? findClosestPopoverElement(menuElement) : null;
        if (!(popoverElement instanceof HTMLElement)) {
            return;
        }

        const searchElement = popoverElement.querySelector<HTMLElement>(
            '[data-menu-search][data-hidden=false]'
        );
        const chromeHeight = searchElement?.getBoundingClientRect().height ?? 0;
        const desiredContentHeight = Math.max(0, MENU_MAX_HEIGHT - chromeHeight);
        const contentMaxHeight =
            isEmpty || hasNoMatches
                ? desiredContentHeight
                : getCachedScrollHintContentMaxHeight(desiredContentHeight);

        popoverElement.style.setProperty(
            '--menu-content-max-height',
            `${chromeHeight + contentMaxHeight}px`
        );

        return () => {
            popoverElement.style.removeProperty('--menu-content-max-height');
        };
    }, [getCachedScrollHintContentMaxHeight, hasNoMatches, isEmpty, menuRef]);

    return (
        <SeparatorContext.Provider value={{ className: 'my-1' }}>
            <HeadingContext.Provider
                value={{ className: 'text-ui-small font-medium text-muted pr-3 pl-3.5 py-1' }}
            >
                <MenuStateContext.Provider value={state}>
                    <div
                        ref={menuRef}
                        role="menu"
                        data-spine-menu=""
                        data-ready={isReady}
                        data-focused={isFocused ? '' : undefined}
                        className="group/menu no-scrollbar min-h-0 flex-1 snap-y snap-proximity scroll-pt-1 overflow-x-hidden overflow-y-auto select-none data-[keyboard-scrolled]:snap-none"
                        tabIndex={-1}
                        onScroll={() => {
                            setSubmenuOpen(false);
                        }}
                        onPointerMoveCapture={(event) => {
                            if (
                                event.target instanceof Element &&
                                menuRef.current?.contains(event.target)
                            ) {
                                state.focus();
                            }
                        }}
                        onMouseDown={(event) => {
                            event.preventDefault();
                        }}
                        style={{ minWidth }}
                    >
                        {isEmpty ? (
                            <MenuPlaceholder>{emptyPlaceholder}</MenuPlaceholder>
                        ) : hasNoMatches ? (
                            <MenuPlaceholder>{noResultsPlaceholder}</MenuPlaceholder>
                        ) : (
                            <MenuVirtualRenderer
                                ref={rendererRef}
                                items={items}
                                onMeasureWidth={updateMinWidth}
                                maxMeasuredWidth={MENU_MAX_WIDTH}
                                estimateSize={(index) => {
                                    const item = items[index];
                                    if (!item) {
                                        return { width: 0, height: 0 };
                                    }
                                    return estimateMenuNodeSize(item);
                                }}
                                renderItem={(virtualItem, item, virtualizer) => {
                                    switch (item.type) {
                                        case 'heading':
                                        case 'placeholder':
                                        case 'separator':
                                            return (
                                                <React.Fragment key={virtualItem.key}>
                                                    {item.element}
                                                </React.Fragment>
                                            );
                                        case 'item': {
                                            const isItemFocused = item.id === focusedItemId;
                                            const focusItem = () => {
                                                state.focus();
                                                setFocusedItemId(item.id);
                                                setSubmenuOpen(true);
                                            };
                                            const subMenuId = `${id}/${virtualItem.key}`;
                                            return (
                                                <MenuItemContext.Provider
                                                    key={virtualItem.key}
                                                    value={{
                                                        ref: virtualizer.measureElement,
                                                        apiRef: isItemFocused
                                                            ? itemAPIRef
                                                            : undefined,
                                                        isFocused: isItemFocused,
                                                        id: item.id,
                                                        hasIconSibling: item.hasIconSibling,
                                                        checkedIndicator,
                                                        'data-index': virtualItem.index,
                                                        titlePrefix: item.parent?.itemElement.props
                                                            .children ? (
                                                            <MenuItemTitlePrefix>
                                                                {
                                                                    item.parent?.itemElement.props
                                                                        .children
                                                                }
                                                            </MenuItemTitlePrefix>
                                                        ) : null,
                                                        onActionStart: () => {
                                                            setFocusedItemId(item.id);
                                                            if (item.children) {
                                                                setSubmenuOpen(true);
                                                            }
                                                        },
                                                        onActionEnd: () => {
                                                            trigger.close();
                                                        },
                                                        onPointerMove: (event) => {
                                                            if (item.disabled) {
                                                                return;
                                                            }
                                                            const submenu = submenuRef.current;
                                                            const latestEvent =
                                                                latestPointerMoveEventRef.current;
                                                            const direction = (() => {
                                                                if (!latestEvent) {
                                                                    return null;
                                                                }
                                                                if (
                                                                    latestEvent.clientX ===
                                                                    event.clientX
                                                                ) {
                                                                    return null;
                                                                }
                                                                return latestEvent.clientX <
                                                                    event.clientX
                                                                    ? 'right'
                                                                    : 'left';
                                                            })();
                                                            latestPointerMoveEventRef.current =
                                                                event;
                                                            if (
                                                                !submenu?.isPointerInGraceArea(
                                                                    event,
                                                                    direction,
                                                                    () => {
                                                                        if (
                                                                            latestPointerMoveEventRef.current ===
                                                                                event &&
                                                                            refs.current.isFocused
                                                                        ) {
                                                                            focusItem();
                                                                        }
                                                                    }
                                                                )
                                                            ) {
                                                                focusItem();
                                                            }
                                                        },
                                                    }}
                                                >
                                                    {item.children ? (
                                                        <SubMenuContext.Provider
                                                            value={{
                                                                id: subMenuId,
                                                                ref: isItemFocused
                                                                    ? submenuRef
                                                                    : undefined,
                                                                onClose: closeOverlay,
                                                                isOpen:
                                                                    trigger.isOpen &&
                                                                    isItemFocused &&
                                                                    isSubmenuOpen,
                                                                animate: !submenuRef.current,
                                                            }}
                                                        >
                                                            {item.element}
                                                        </SubMenuContext.Provider>
                                                    ) : (
                                                        item.element
                                                    )}
                                                </MenuItemContext.Provider>
                                            );
                                        }
                                        default:
                                            assertNever(item);
                                    }
                                }}
                            />
                        )}
                    </div>
                </MenuStateContext.Provider>
            </HeadingContext.Provider>
        </SeparatorContext.Provider>
    );
};
